import React, { useState, useEffect } from 'react';
import { projectAPI, wardAPI, countyAPI, budgetAPI } from '../services/api';
import { Project, Ward, Analytics } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const DashboardPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [budgets, setBudgets] = useState<any[]>([]);
  const [fiscalYears, setFiscalYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWard, setSelectedWard] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'analytics' | 'projects' | 'budgets'>('projects');
  const [filters, setFilters] = useState({
    sector: '',
    status: '',
    search: '',
    fiscal_year: '',
    current_only: false
  });
  const [analyticsYear, setAnalyticsYear] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const [projectsRes, wardsRes, analyticsRes, budgetsRes, fyRes] = await Promise.all([
        projectAPI.getPublic('Turkana').catch(() => ({ data: [] })),
        wardAPI.getByCounty('Turkana').catch(() => ({ data: [] })),
        countyAPI.getAnalytics('Turkana').catch(() => ({ data: null })),
        budgetAPI.getAll().catch(() => ({ data: [] })),
        fetch(`${API_BASE}/projects/fiscal-years`).then(r => r.ok ? r.json() : []).catch(() => [])
      ]);
      
      const projectsData = projectsRes.data || [];
      const budgetsData = Array.isArray(budgetsRes.data) ? budgetsRes.data : [];
      setProjects(projectsData);
      setWards(wardsRes.data || []);
      setAnalytics(analyticsRes.data || null);
      setBudgets(budgetsData);

      const apiYears: string[] = Array.isArray(fyRes) ? fyRes : [];
      const projectYears = projectsData.map((p: any) => p.fiscal_year).filter(Boolean);
      const budgetYears = budgetsData.map((b: any) => b.fiscal_year).filter(Boolean);
      const merged = Array.from(new Set([...apiYears, ...projectYears, ...budgetYears]))
        .sort((a: string, b: string) => b.localeCompare(a));
      setFiscalYears(merged);
    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
      
      // Set error message
      if (error.message.includes('timeout')) {
        setError('Connection timeout. The server may be starting up.');
      } else if (error.message.includes('503')) {
        setError('Service temporarily unavailable. The server is starting up.');
      } else {
        setError('Unable to connect to server.');
      }
      
      // Don't set fallback data - show empty state instead
      setProjects([]);
      setBudgets([]);
      setWards([]);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  const getProjectsByStatus = () => {
    const statusCounts = projects.reduce((acc, project) => {
      acc[project.status] = (acc[project.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return statusCounts;
  };

  const getProjectsBySector = () => {
    const sectorCounts = projects.reduce((acc, project) => {
      acc[project.sector] = (acc[project.sector] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return sectorCounts;
  };

  const getBudgetBySector = () => {
    const sectorBudgets = projects.reduce((acc, project) => {
      acc[project.sector] = (acc[project.sector] || 0) + project.budget;
      return acc;
    }, {} as Record<string, number>);
    return sectorBudgets;
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = !filters.search || 
      project.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      project.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
      project.contractor?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesFiscalYear = !filters.fiscal_year || project.fiscal_year === filters.fiscal_year;
    const matchesCurrent = !filters.current_only || project.is_current_budget === true;
    return (
      matchesSearch && matchesFiscalYear && matchesCurrent &&
      (!selectedWard || project.ward_id === selectedWard) &&
      (!filters.sector || project.sector === filters.sector) &&
      (!filters.status || project.status === filters.status)
    );
  });

  const exportProjects = () => {
    const headers = ['Name', 'Ward', 'Sub Location', 'Sector', 'Budget (KES)', 'Status', 'Financial Year', 'Description'];
    const rows = filteredProjects.map(p => [
      p.name,
      wards.find(w => w.id === p.ward_id)?.name || p.ward_id,
      p.sub_location || '',
      p.sector,
      p.budget,
      p.status,
      p.fiscal_year || '',
      (p.description || '').replace(/,/g, ';')
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `projects${filters.fiscal_year ? '_' + filters.fiscal_year.replace('/', '-') : ''}${filters.sector ? '_' + filters.sector : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <LoadingSpinner 
        message="Loading dashboard data..." 
        showRetryMessage={true}
        onRetry={loadData}
      />
    );
  }

  const statusData = getProjectsByStatus();
  const sectorData = getProjectsBySector();
  const budgetData = getBudgetBySector();

  return (
    <div className="dashboard-page">
      {error && (
        <div className="error-banner">
          <p>⚠️ {error}</p>
          <p><button onClick={loadData} className="retry-link">Retry</button></p>
        </div>
      )}
      
      <header className="page-header">
        <div className="icon icon-primary">📊</div>
        <h1>Projects & Analytics Dashboard</h1>
        <p>Comprehensive view of county development projects and analytics across Turkana</p>
      </header>

      <section className="view-toggle">
        <div className="toggle-buttons">
          <button 
            className={`toggle-btn ${viewMode === 'projects' ? 'active' : ''}`}
            onClick={() => setViewMode('projects')}
          >
            🏗️ Projects Directory
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'analytics' ? 'active' : ''}`}
            onClick={() => setViewMode('analytics')}
          >
            📊 Analytics View
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'budgets' ? 'active' : ''}`}
            onClick={() => setViewMode('budgets')}
          >
            💰 Budget Overview
          </button>
        </div>
      </section>

      <section className="dashboard-filters">
        <div className="filter-group">
          <label>Filter by Ward:</label>
          <select 
            value={selectedWard} 
            onChange={(e) => setSelectedWard(e.target.value)}
          >
            <option value="">All Wards</option>
            {wards.map(ward => (
              <option key={ward.id} value={ward.id}>{ward.name}</option>
            ))}
          </select>
        </div>
        
        {viewMode === 'projects' && (
          <>
            <div className="filter-group">
              <label>Search Projects:</label>
              <input
                type="text"
                placeholder="Search by name, description, or contractor..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="search-input"
              />
            </div>
            
            <div className="filter-group">
              <label>Sector:</label>
              <select 
                value={filters.sector} 
                onChange={(e) => setFilters({...filters, sector: e.target.value})}
              >
                <option value="">All Sectors</option>
                <option value="Health">Health</option>
                <option value="Infrastructure">Infrastructure</option>
                <option value="Water">Water</option>
                <option value="Education">Education</option>
                <option value="Agriculture">Agriculture</option>
                <option value="Energy">Energy</option>
                <option value="Trade">Trade</option>
                <option value="General">General</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={filters.status} 
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All Status</option>
                <option value="planned">Planned</option>
                <option value="procurement">Procurement</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="stalled">Stalled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Budget Year:</label>
              <select
                value={filters.fiscal_year}
                onChange={(e) => setFilters({...filters, fiscal_year: e.target.value, current_only: false})}
              >
                <option value="">All Years</option>
                {fiscalYears.map(fy => (
                  <option key={fy} value={fy}>{fy}</option>
                ))}
              </select>
            </div>

            <div className="filter-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={filters.current_only}
                  onChange={(e) => setFilters({...filters, current_only: e.target.checked, fiscal_year: ''})}
                  style={{ marginRight: '0.4rem' }}
                />
                Current Budget Only
              </label>
            </div>
          </>
        )}
      </section>

      <section className="dashboard-stats">
        <div className="stats-grid">
          <div className="stat-card">
            <h3>🏗️ Total Projects</h3>
            <p className="stat-value">{filteredProjects.length}</p>
          </div>
          <div className="stat-card">
            <h3>💰 Total Budget</h3>
            <p className="stat-value">KES {budgets.length > 0 ? (budgets[0].total_budget / 1e9).toFixed(1) : (filteredProjects.reduce((sum, p) => sum + p.budget, 0) / 1e9).toFixed(1)}B</p>
          </div>
          <div className="stat-card">
            <h3>✅ Completed</h3>
            <p className="stat-value">{filteredProjects.filter(p => p.status === 'completed').length}</p>
          </div>
          <div className="stat-card">
            <h3>🔄 Active</h3>
            <p className="stat-value">{filteredProjects.filter(p => ['ongoing', 'procurement'].includes(p.status)).length}</p>
          </div>
        </div>
      </section>

      {viewMode === 'analytics' && (
        <>
          <section className="dashboard-filters" style={{ paddingTop: 0 }}>
            <div className="filter-group">
              <label>Financial Year:</label>
              <select value={analyticsYear} onChange={(e) => setAnalyticsYear(e.target.value)}>
                <option value="">All Years</option>
                {fiscalYears.map(fy => <option key={fy} value={fy}>{fy}</option>)}
              </select>
            </div>
          </section>
          {projects.filter(p => !analyticsYear || p.fiscal_year === analyticsYear).length === 0 ? (
            <section className="empty-state">
              <div className="empty-state-content">
                <div className="empty-icon">📊</div>
                <h2>No Projects Data Available</h2>
                <p>This is a live system demo with no fallback data. To see projects and analytics:</p>
                <ol className="empty-instructions">
                  <li>Go to the Institutional Responder dashboard</li>
                  <li>Upload the sample Excel file: <code>sample_budget_projects.xlsx</code></li>
                  <li>The system will automatically extract ward budgets and projects</li>
                  <li>Projects will appear here immediately after upload</li>
                </ol>
                <div className="empty-actions">
                  <button onClick={loadData} className="btn-primary">🔄 Refresh Data</button>
                </div>
              </div>
            </section>
          ) : (
            <>
          <section className="dashboard-charts">
            <div className="chart-container">
              <h2>Projects by Status{analyticsYear ? ` — FY ${analyticsYear}` : ''}</h2>
              <div className="chart-bars">
                {Object.entries(
                  projects
                    .filter(p => !analyticsYear || p.fiscal_year === analyticsYear)
                    .reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {} as Record<string,number>)
                ).map(([status, count], _, arr) => (
                  <div key={status} className="chart-bar">
                    <div className="bar-label">{status}</div>
                    <div className="bar-container">
                      <div
                        className={`bar bar-${status}`}
                        style={{ width: `${(count / Math.max(...arr.map(([,v]) => v))) * 100}%` }}
                      ></div>
                      <span className="bar-value">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="chart-container">
              <h2>Projects by Sector{analyticsYear ? ` — FY ${analyticsYear}` : ''}</h2>
              <div className="chart-bars">
                {Object.entries(
                  projects
                    .filter(p => !analyticsYear || p.fiscal_year === analyticsYear)
                    .reduce((acc, p) => { acc[p.sector] = (acc[p.sector] || 0) + 1; return acc; }, {} as Record<string,number>)
                ).map(([sector, count], _, arr) => (
                  <div key={sector} className="chart-bar">
                    <div className="bar-label">{sector}</div>
                    <div className="bar-container">
                      <div
                        className="bar bar-sector"
                        style={{ width: `${(count / Math.max(...arr.map(([,v]) => v))) * 100}%` }}
                      ></div>
                      <span className="bar-value">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="dashboard-map">
            <div className="map-container">
              <h2>Project Distribution Map</h2>
              <div className="ward-grid">
                {wards.map(ward => {
                  const wardProjects = projects.filter(p => p.ward_id === ward.id);
                  const completedProjects = wardProjects.filter(p => p.status === 'completed').length;
                  const totalProjects = wardProjects.length;
                  const completionRate = totalProjects > 0 ? (completedProjects / totalProjects) * 100 : 0;
                  
                  return (
                    <div 
                      key={ward.id} 
                      className={`ward-tile ${selectedWard === ward.id ? 'selected' : ''}`}
                      onClick={() => setSelectedWard(selectedWard === ward.id ? '' : ward.id)}
                    >
                      <div className="ward-name">{ward.name}</div>
                      <div className="ward-stats">
                        <div className="ward-projects">{totalProjects} projects</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="recent-projects">
            <h2>Recent Projects{analyticsYear ? ` — FY ${analyticsYear}` : ''}</h2>
            <div className="projects-timeline">
              {projects
                .filter(p => !analyticsYear || p.fiscal_year === analyticsYear)
                .filter(p => !selectedWard || p.ward_id === selectedWard)
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .slice(0, 5)
                .map(project => {
                  const ward = wards.find(w => w.id === project.ward_id);
                  return (
                    <div key={project.id} className="timeline-item">
                      <div className="timeline-date">{new Date(project.created_at).toLocaleDateString()}</div>
                      <div className="timeline-content">
                        <h4>{project.name}</h4>
                        <p className="project-location">
                          📍 {ward?.name} Ward{project.sub_location ? ` • ${project.sub_location}` : ''} • {project.sector}
                        </p>
                        <p style={{ fontSize: '0.85rem', color: '#555', margin: '0.2rem 0' }}>
                          💰 KES {(project.budget / 1e6).toFixed(2)}M
                        </p>
                        {project.description && <p className="update-description">{project.description}</p>}
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>
            </>
          )}
        </>
      )}

      {viewMode === 'budgets' && (
        <section className="budget-overview">
          <div className="section-header">
            <h2>County Budget Overview</h2>
            <p>Approved budget allocations by sector and fiscal year</p>
          </div>
          
          {budgets.length > 0 ? (
            <div className="budget-cards">
              {budgets.map(budget => (
                <div key={budget.id} className="budget-card">
                  <div className="budget-header">
                    <h3>FY {budget.fiscal_year}</h3>
                    <span className={`status-badge ${budget.status}`}>
                      {budget.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="budget-total">
                    <h4>Total Budget</h4>
                    <p className="budget-amount">KES {(budget.total_budget / 1e9).toFixed(1)}B</p>
                  </div>
                  
                  <div className="sector-allocations">
                    <h4>Sector Allocations</h4>
                    <div className="allocation-bars">
                      {Object.entries(budget.sector_allocations || {}).map(([sector, amount]) => {
                        const percentage = ((amount as number) / budget.total_budget) * 100;
                        return (
                          <div key={sector} className="allocation-bar">
                            <div className="allocation-label">
                              <span>{sector}</span>
                              <span>KES {((amount as number) / 1e9).toFixed(1)}B ({percentage.toFixed(1)}%)</span>
                            </div>
                            <div className="allocation-progress">
                              <div 
                                className="allocation-fill"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="budget-meta">
                    <p><strong>Uploaded:</strong> {new Date(budget.uploaded_at).toLocaleDateString()}</p>
                    <p><strong>Status:</strong> {budget.is_approved ? 'Approved' : 'Pending'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-budgets">
              <p>No budget data available.</p>
            </div>
          )}
        </section>
      )}

      {viewMode === 'projects' && (
        <section className="projects-grid">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button
              onClick={exportProjects}
              disabled={filteredProjects.length === 0}
              style={{ padding: '0.5rem 1.2rem', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '6px', cursor: filteredProjects.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredProjects.length === 0 ? 0.5 : 1, fontWeight: 600 }}
            >
              ⬇️ Export CSV ({filteredProjects.length} projects)
            </button>
          </div>
          {filteredProjects.map(project => {
            const ward = wards.find(w => w.id === project.ward_id);
            return (
              <div key={project.id} className="project-card">
                <div className="project-header">
                  <h3>{project.name}</h3>
                  <span className={`status-badge ${project.status}`}>
                    {project.status.toUpperCase()}
                  </span>
                </div>
                
                <div className="project-info">
                  <p><strong>📍 Location:</strong> {ward?.name || 'Unknown'} Ward{project.sub_location ? ` • ${project.sub_location}` : ''}</p>
                  <p><strong>🏢 Sector:</strong> {project.sector}</p>
                  <p><strong>💰 Budget:</strong> KES {(project.budget / 1e6).toFixed(1)}M</p>
                  {project.contractor && <p><strong>🏗️ Contractor:</strong> {project.contractor}</p>}
                  {project.start_date && <p><strong>📅 Start Date:</strong> {new Date(project.start_date).toLocaleDateString()}</p>}
                  {project.expected_completion && <p><strong>🎯 Target Completion:</strong> {new Date(project.expected_completion).toLocaleDateString()}</p>}
                  {project.fiscal_year && (
                    <p><strong>📆 Budget Year:</strong>
                      <span style={{ marginLeft: '0.4rem', padding: '1px 8px', borderRadius: '10px', fontSize: '0.8rem',
                        background: project.is_current_budget ? '#e3f2fd' : '#f5f5f5',
                        color: project.is_current_budget ? '#1565c0' : '#757575' }}>
                        {project.fiscal_year} {project.is_current_budget ? '★ Current' : '🗃️ Archived'}
                      </span>
                    </p>
                  )}
                  {project.approval_status && <p><strong>📄 Approval:</strong> <span className={`status-badge ${project.approval_status}`}>{project.approval_status}</span></p>}
                </div>

                {project.description && (
                  <div className="project-description">
                    <p>{project.description}</p>
                  </div>
                )}

                {project.updates && project.updates.length > 0 && (
                  <div className="project-updates">
                    <h4>Latest Update:</h4>
                    <p>{project.updates[project.updates.length - 1].description}</p>
                    <small>{new Date(project.updates[project.updates.length - 1].date).toLocaleDateString()}</small>
                  </div>
                )}
              </div>
            );
          })}
          
          {filteredProjects.length === 0 && (
            <div className="no-projects">
              <p>No projects found matching the selected filters.</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default DashboardPage;